import React from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Column } from '../../components/DataTable/DataTable';
import { APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import {
  StandardTableActions,
  TableActionButton,
  Badge,
  CopyIcon,
  StarIcon,
} from '../../components/ui';
import type { OfferPdfLayoutListItem } from './useData';
import { deleteOfferPdfLayout, setDefaultOfferPdfLayout, createOfferPdfLayout } from './useData';
import type { OfferPdfTemplateDefinition } from '../../utils/offerPdfTypes';

const ROUTES = {
  list: '/settings/offer-pdf-layouts',
  new: '/settings/offer-pdf-layouts/new',
  edit: (id: number) => `/settings/offer-pdf-layouts/${id}/edit`,
};

export const moduleConfig = {
  module: {
    title: 'Offer PDF layouts',
    description: 'Create and edit PDF styles for offer exports.',
    route: {
      list: ROUTES.list,
      new: ROUTES.new,
      detail: (id: number) => ROUTES.edit(id),
      edit: ROUTES.edit,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search layouts by name...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        type: 'text' as const,
        getValue: (item: OfferPdfLayoutListItem) => item.name,
        options: [],
      },
    ],
    applyFilter: (
      items: OfferPdfLayoutListItem[],
      filters: { name?: string },
      _dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      if (!filters?.name || String(filters.name).trim() === '') return items;
      const term = String(filters.name).toLowerCase();
      return items.filter((item) => item.name?.toLowerCase().includes(term));
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
    },
  ],
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New layout',
        showTitle: false,
        variant: 'primary' as const,
        href: ROUTES.new,
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: OfferPdfLayoutListItem) => {
      window.location.href = ROUTES.edit(item.id);
    },
    keyExtractor: (item: OfferPdfLayoutListItem) => item.id,
  },
  emptyStates: {
    noData: 'No layouts yet. Create a layout for offer PDF exports.',
    noSearchResults: 'No layouts match your search.',
    noFilterResults: 'No layouts match the selected filters.',
  },
  delete: {
    title: 'Delete layout',
    message: 'Are you sure you want to delete this layout?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete layout.',
    handler: deleteOfferPdfLayout,
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString() || '—';
    },
  },
  getColumns: (
    formatDate: (dateString?: string) => string,
    colors?: ThemeColors
  ): Column<OfferPdfLayoutListItem>[] => {
    const textMuted = colors?.textMuted || '';
    return [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        render: (value, row) => (
          <Link
            to={ROUTES.edit(row.id)}
            className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {value || row.name || '—'}
          </Link>
        ),
      },
      {
        key: 'isDefault',
        header: 'Default',
        sortable: true,
        render: (_value, row) =>
          row.isDefault ? (
            <Badge variant="success">Default</Badge>
          ) : (
            <span className={`text-sm ${textMuted}`}>—</span>
          ),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        sortable: true,
        render: (value) => formatDate(value),
      },
    ];
  },
  getToolbarIcon: (iconName: string): React.ReactNode => {
    const iconClass = 'w-5 h-5';
    const svgProps = { className: iconClass, fill: 'none' as const, stroke: 'currentColor' as const, viewBox: '0 0 24 24' };
    switch (iconName) {
      case 'add':
        return (
          <svg {...svgProps}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      default:
        return null;
    }
  },
  getTableActions: (
    item: OfferPdfLayoutListItem,
    onDelete: (id: number) => void,
    _colors?: unknown,
    listContext?: { refresh?: () => void },
    options?: { canDelete?: boolean; canEdit?: boolean }
  ): React.ReactNode => {
    const canDelete = options?.canDelete !== false;
    const canEdit = options?.canEdit !== false;
    const handleSetDefault = async () => {
      await setDefaultOfferPdfLayout(item.id);
      listContext?.refresh?.();
    };
    const handleDuplicate = async () => {
      try {
        const def = item.definition as OfferPdfTemplateDefinition;
        const created = await createOfferPdfLayout({
          name: `Copy of ${item.name}`,
          definition: JSON.parse(JSON.stringify(def ?? {})),
          isDefault: false,
        });
        if (created?.id != null) {
          window.location.href = ROUTES.edit(created.id);
        } else {
          listContext?.refresh?.();
        }
      } catch (err) {
        console.error('Failed to duplicate layout:', err);
        toast.error(err instanceof Error ? err.message : 'Failed to duplicate layout');
      }
    };
    return (
      <StandardTableActions
        editTo={ROUTES.edit(item.id)}
        onDelete={() => onDelete(item.id)}
        canEdit={canEdit}
        canDelete={canDelete}
      >
        {!item.isDefault && (
          <TableActionButton
            title="Set as default"
            onClick={() => {
              void handleSetDefault();
            }}
          >
            <StarIcon className="h-4 w-4" />
          </TableActionButton>
        )}
        <TableActionButton
          title="Duplicate"
          onClick={() => {
            void handleDuplicate();
          }}
        >
          <CopyIcon className="h-4 w-4" />
        </TableActionButton>
      </StandardTableActions>
    );
  },
};

export const getColumns = moduleConfig.getColumns;
export const getToolbarIcon = moduleConfig.getToolbarIcon;
export const getTableActions = moduleConfig.getTableActions;
